import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, WordsReveal } from "@/components/site/Reveal";
import LazyImage from "@/components/site/LazyImage";

// Reusable cinematic split section with scroll-based image motion
export default function FeatureSection({
  overline,
  title,
  intro,
  images = [],
  list = [],
  cta,
  reverse = false,
  dark = false,
  fit = "cover",
  testId,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 70]);

  const bg = dark ? "bg-forest text-ivory" : "bg-ivory text-charcoal";
  const overlineColor = dark ? "text-gold" : "text-brown";
  const titleColor = dark ? "text-ivory" : "text-forest";
  const bodyColor = dark ? "text-ivory/75" : "text-charcoal/70";

  return (
    <section ref={ref} data-testid={testId} className={`relative overflow-hidden py-14 md:py-20 ${bg}`}>
      <div className={`mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:grid-cols-2 md:gap-16 md:px-10`}>
        <div className={reverse ? "md:order-2" : ""}>
          <Reveal><p className={`mb-6 text-xs uppercase tracking-widest-2 md:text-sm ${overlineColor}`}>{overline}</p></Reveal>
          <h2 className={`font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl ${titleColor}`}>
            <WordsReveal text={title} />
          </h2>
          <Reveal delay={0.15}><p className={`mt-7 max-w-md ${bodyColor}`}>{intro}</p></Reveal>

          {list.length > 0 && (
            <Reveal delay={0.25}>
              <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {list.map((item) => (
                  <li key={item} className={`flex items-center gap-3 ${bodyColor}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          )}

          {cta && (
            <Reveal delay={0.35}>
              <Link
                to={cta.to}
                data-testid={`${testId}-cta`}
                className={`group mt-9 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm transition-all duration-300 hover:-translate-y-0.5 ${
                  dark ? "border border-ivory/30 hover:bg-ivory hover:text-forest" : "bg-forest text-ivory hover:bg-moss"
                }`}
              >
                {cta.label}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          )}
        </div>

        <div className={`relative h-[440px] md:h-[560px] ${reverse ? "md:order-1" : ""}`}>
          <motion.div style={{ y: y1 }} className="absolute right-0 top-0 z-10 w-[74%]">
            <LazyImage src={images[0]} alt={title} fit={fit} className="aspect-[4/5] rounded-2xl" />
          </motion.div>
          {images[1] && (
            <motion.div style={{ y: y2 }} className="absolute bottom-0 left-0 z-20 w-[48%]">
              <LazyImage src={images[1]} alt={title} fit={fit} className="aspect-square rounded-2xl shadow-[0_20px_50px_rgba(26,54,38,0.25)]" />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
